import { useState, useMemo, useEffect, useCallback } from 'react';
import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { EntityIndexLayout, WorkspaceShell } from '@/components/layouts';
import { PaymentDetailPanel } from '@/components/organisms/payments/PaymentDetailPanel';
import {
  PAYMENT_SORT_OPTIONS,
  PaymentsListPanel,
} from '@/components/organisms/payments/PaymentsListPanel';
import {
  type PaymentMethodFilter,
  type PaymentSyncFilter,
  type PaymentTableFilters,
  type PaymentTableSortColumn,
  type PaymentTableSortDir,
  PaymentsTableView,
} from '@/components/organisms/payments/PaymentsTableView';
import { STATUS_FILTERS, type StatusFilter } from '@/components/organisms/payments/payments-shared';
import { usePayments, type PaymentRecord } from '@/hooks/payments/usePayments';
import { useIsFieldHidden } from '@/hooks/usePermissions';

type SplitSortBy = 'createdAt' | 'updatedAt' | 'amount' | 'status';

interface PaymentRouteSearch {
  q?: string;
  status?: StatusFilter;
  page?: number;
  dateFrom?: string;
  dateTo?: string;
  paymentMethod?: PaymentMethodFilter;
  transactionId?: string;
  amountOp?: PaymentTableFilters['amount']['op'];
  amountValue?: string;
  syncStatus?: PaymentSyncFilter;
}

type ResolvedPaymentRouteSearch = Required<Omit<PaymentRouteSearch, 'dateFrom' | 'dateTo'>> &
  Pick<PaymentRouteSearch, 'dateFrom' | 'dateTo'>;

const METHOD_FILTERS: PaymentMethodFilter[] = [
  'ALL',
  'CARD',
  'ACH',
  'CHECK',
  'CASH',
  'FINANCING',
  'OTHER',
];
const SYNC_FILTERS: PaymentSyncFilter[] = ['ALL', 'SYNCED', 'NOT_SYNCED'];
const AMOUNT_OPS: Array<PaymentTableFilters['amount']['op']> = ['eq', 'gt', 'lt', 'gte', 'lte'];

export const Route = createFileRoute('/_dashboard/dashboard/payments')({
  validateSearch: (search: Record<string, unknown>): PaymentRouteSearch => ({
    q: readString(search.q),
    status: readEnum(search.status, STATUS_FILTERS, 'ALL'),
    page: readPositiveInt(search.page, 1),
    dateFrom: readOptionalString(search.dateFrom),
    dateTo: readOptionalString(search.dateTo),
    paymentMethod: readEnum(search.paymentMethod, METHOD_FILTERS, 'ALL'),
    transactionId: readString(search.transactionId),
    amountOp: readEnum(search.amountOp, AMOUNT_OPS, 'eq'),
    amountValue: readString(search.amountValue),
    syncStatus: readEnum(search.syncStatus, SYNC_FILTERS, 'ALL'),
  }),
  component: PaymentsPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function readString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

function readOptionalString(value: unknown): string | undefined {
  const text = readString(value);
  return text || undefined;
}

function readPositiveInt(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
}

function readEnum<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return options.includes(value as T) ? (value as T) : fallback;
}

function parseRouteDate(value?: string) {
  if (!value) return undefined;
  const date = parseISO(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function formatRouteDate(date: Date | undefined) {
  return date ? format(date, 'yyyy-MM-dd') : undefined;
}

function cleanPaymentSearch(next: Record<string, unknown>) {
  const defaults: Partial<Record<keyof PaymentRouteSearch, unknown>> = {
    q: '',
    status: 'ALL',
    page: 1,
    paymentMethod: 'ALL',
    transactionId: '',
    amountOp: 'eq',
    amountValue: '',
    syncStatus: 'ALL',
  };

  (Object.keys(next) as Array<keyof PaymentRouteSearch>).forEach((key) => {
    if (next[key] == null || next[key] === defaults[key]) {
      delete next[key];
    }
  });

  return next;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function PaymentsPage() {
  const router = useRouter();
  const pricingHidden = useIsFieldHidden('PRICING');
  const paymentInfoHidden = useIsFieldHidden('PAYMENT_INFO');
  const paymentDetailsHidden = pricingHidden || paymentInfoHidden;
  const { location } = useRouterState();
  const searchParams = Route.useSearch();
  const filters = useMemo<ResolvedPaymentRouteSearch>(
    () => ({
      q: searchParams.q ?? '',
      status: searchParams.status ?? 'ALL',
      page: searchParams.page ?? 1,
      dateFrom: searchParams.dateFrom,
      dateTo: searchParams.dateTo,
      paymentMethod: searchParams.paymentMethod ?? 'ALL',
      transactionId: searchParams.transactionId ?? '',
      amountOp: searchParams.amountOp ?? 'eq',
      amountValue: searchParams.amountValue ?? '',
      syncStatus: searchParams.syncStatus ?? 'ALL',
    }),
    [searchParams],
  );

  const [splitSort, setSplitSort] = useState<{
    sortBy: SplitSortBy;
    sortDir: PaymentTableSortDir;
  }>({ sortBy: 'createdAt', sortDir: 'desc' });
  const [tableSort, setTableSort] = useState<{
    sortCol: PaymentTableSortColumn;
    sortDir: PaymentTableSortDir;
  }>({ sortCol: 'date', sortDir: 'desc' });

  const idMatch = location.pathname.match(/\/dashboard\/payments\/([^/]+)/);
  const selectedId = idMatch?.[1];
  const hasChildRoute = Boolean(selectedId);
  const isIndexRoute = location.pathname === '/dashboard/payments';

  useEffect(() => {
    const legacyPaymentId = new URLSearchParams(window.location.search).get('paymentId');
    if (!legacyPaymentId) return;
    void router.navigate({
      to: '/dashboard/payments/$id',
      params: { id: legacyPaymentId },
      search: (prev) => cleanPaymentSearch({ ...prev }) as PaymentRouteSearch,
      replace: true,
    });
  }, [router]);

  const tableFilters = useMemo<PaymentTableFilters>(
    () => ({
      search: filters.q,
      status: filters.status,
      method: filters.paymentMethod,
      transactionId: filters.transactionId,
      amount: {
        op: filters.amountOp,
        value: filters.amountValue,
      },
      date:
        filters.dateFrom || filters.dateTo
          ? ({
              from: parseRouteDate(filters.dateFrom),
              to: parseRouteDate(filters.dateTo),
            } satisfies DateRange)
          : undefined,
      syncStatus: filters.syncStatus,
    }),
    [
      filters.amountOp,
      filters.amountValue,
      filters.dateFrom,
      filters.dateTo,
      filters.paymentMethod,
      filters.q,
      filters.status,
      filters.syncStatus,
      filters.transactionId,
    ],
  );

  function updateSearch(patch: Partial<PaymentRouteSearch>) {
    // Use router.navigate (relative to the CURRENT location) instead of
    // Route.useNavigate() (bound to the parent `/dashboard/payments` route).
    // The latter dropped the active `/$id` child — so a fresh-mounting table
    // (which fires a grid sort/page event on mount while a detail sheet is open)
    // bounced the detail back to the index. Mirrors invoices.tsx.
    void router.navigate({
      search: (prev) => {
        const next = { ...prev, ...patch } as Record<string, unknown>;
        return cleanPaymentSearch(next) as never;
      },
      replace: true,
    });
  }

  function handleStatus(s: StatusFilter) {
    updateSearch({ status: s, page: 1 });
  }

  function handleDateChange(range: { dateFrom?: string; dateTo?: string }) {
    updateSearch({ dateFrom: range.dateFrom, dateTo: range.dateTo, page: 1 });
  }

  function handleListSortOption(index: number) {
    const next = PAYMENT_SORT_OPTIONS[index] ?? PAYMENT_SORT_OPTIONS[0];
    setSplitSort({ sortBy: next.sortBy as SplitSortBy, sortDir: next.sortDir });
    updateSearch({ page: 1 });
  }

  function handleTableFiltersChange(patch: Partial<PaymentTableFilters>) {
    const next: Partial<PaymentRouteSearch> = { page: 1 };

    if ('search' in patch) next.q = patch.search ?? '';
    if ('status' in patch) next.status = patch.status ?? 'ALL';
    if ('method' in patch) next.paymentMethod = patch.method ?? 'ALL';
    if ('transactionId' in patch) next.transactionId = patch.transactionId ?? '';
    if ('syncStatus' in patch) next.syncStatus = patch.syncStatus ?? 'ALL';
    if ('date' in patch) {
      next.dateFrom = formatRouteDate(patch.date?.from);
      next.dateTo = formatRouteDate(patch.date?.to);
    }
    if ('amount' in patch) {
      next.amountOp = patch.amount?.op ?? 'eq';
      next.amountValue = patch.amount?.value ?? '';
    }

    updateSearch(next);
  }

  function handleTableSortChange(sort: {
    sortCol: PaymentTableSortColumn;
    sortDir: PaymentTableSortDir;
  }) {
    setTableSort(sort);
    updateSearch({ page: 1 });
  }

  const navigatePayment = useCallback(
    (payment: PaymentRecord, replace = false) => {
      void router.navigate({
        to: '/dashboard/payments/$id',
        params: { id: payment.id },
        search: (prev) => prev as never,
        replace,
      });
    },
    [router],
  );

  function handleSelect(payment: PaymentRecord) {
    navigatePayment(payment);
  }

  const handleAutoSelect = useCallback(
    (payment: PaymentRecord) => {
      navigatePayment(payment, true);
    },
    [navigatePayment],
  );

  function handleOpenPayment(payment: PaymentRecord) {
    handleSelect(payment);
  }

  return (
    <WorkspaceShell>
      <EntityIndexLayout
        title="Payments"
        storageKey="payments-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() =>
          void router.navigate({ to: '/dashboard/payments', search: (prev) => prev as never })
        }
        splitWidthClass="w-[360px]"
        tableSheetWidthClass="w-[620px] sm:max-w-[620px]"
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        splitList={() => (
          <div className="flex h-full flex-col">
            <PaymentsSplitList
              filters={filters}
              selectedId={selectedId ?? null}
              autoSelectFirst={isIndexRoute && !paymentDetailsHidden}
              detailsHidden={paymentDetailsHidden}
              splitSort={splitSort}
              onSelect={handleSelect}
              onAutoSelect={handleAutoSelect}
              onStatus={handleStatus}
              onSearch={(value) => updateSearch({ q: value, page: 1 })}
              onSortOption={handleListSortOption}
              onDateChange={handleDateChange}
              onAdvancedChange={updateSearch}
              onPage={(nextPage) => updateSearch({ page: nextPage })}
            />
          </div>
        )}
        splitDetail={
          hasChildRoute ? (
            <Outlet />
          ) : (
            <PaymentDetailPanel
              payment={null}
              onRefund={() => undefined}
              onManageInStripe={() => undefined}
            />
          )
        }
        tableView={({ openInSheet }) => (
          <PaymentsTableView
            onOpenPayment={(payment) =>
              openInSheet(
                () =>
                  void router.navigate({
                    to: '/dashboard/payments/$id',
                    params: { id: payment.id },
                    search: (prev) => prev as never,
                  }),
              )
            }
            filters={tableFilters}
            onFiltersChange={handleTableFiltersChange}
            page={filters.page}
            onPageChange={(nextPage) => updateSearch({ page: nextPage })}
            sortCol={tableSort.sortCol}
            sortDir={tableSort.sortDir}
            onSortChange={handleTableSortChange}
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="md:hidden flex flex-col flex-1 min-h-0">
            {hasChildRoute ? (
              <Outlet />
            ) : (
              <PaymentsTableView
                onOpenPayment={handleOpenPayment}
                filters={tableFilters}
                onFiltersChange={handleTableFiltersChange}
                page={filters.page}
                onPageChange={(nextPage) => updateSearch({ page: nextPage })}
                sortCol={tableSort.sortCol}
                sortDir={tableSort.sortDir}
                onSortChange={handleTableSortChange}
              />
            )}
          </div>
        }
      />
    </WorkspaceShell>
  );
}

interface PaymentsSplitListProps {
  filters: ResolvedPaymentRouteSearch;
  selectedId: string | null;
  autoSelectFirst: boolean;
  splitSort: { sortBy: SplitSortBy; sortDir: PaymentTableSortDir };
  onSelect: (payment: PaymentRecord) => void;
  onAutoSelect: (payment: PaymentRecord) => void;
  onStatus: (status: StatusFilter) => void;
  onSearch: (value: string) => void;
  onSortOption: (index: number) => void;
  onDateChange: (range: { dateFrom?: string; dateTo?: string }) => void;
  onAdvancedChange: (patch: Partial<PaymentRouteSearch>) => void;
  onPage: (page: number) => void;
  detailsHidden: boolean;
}

function PaymentsSplitList({
  filters,
  selectedId,
  autoSelectFirst,
  splitSort,
  onSelect,
  onAutoSelect,
  onStatus,
  onSearch,
  onSortOption,
  onDateChange,
  onAdvancedChange,
  onPage,
  detailsHidden,
}: PaymentsSplitListProps) {
  const pricingHidden = useIsFieldHidden('PRICING');
  const paymentInfoHidden = useIsFieldHidden('PAYMENT_INFO');
  const amountHidden = pricingHidden || paymentInfoHidden;
  const amountValue = Number(filters.amountValue);
  const hasAmountFilter = filters.amountValue.trim() !== '' && Number.isFinite(amountValue);
  const query = usePayments({
    page: filters.page,
    limit: 30,
    search: filters.q.trim() || undefined,
    status: filters.status === 'ALL' ? undefined : filters.status,
    paymentMethod:
      !paymentInfoHidden && filters.paymentMethod !== 'ALL' ? filters.paymentMethod : undefined,
    transactionId: !paymentInfoHidden ? filters.transactionId.trim() || undefined : undefined,
    amountOp: !amountHidden && hasAmountFilter ? filters.amountOp : undefined,
    amountValue: !amountHidden && hasAmountFilter ? amountValue : undefined,
    syncStatus: !paymentInfoHidden && filters.syncStatus !== 'ALL' ? filters.syncStatus : undefined,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    sortBy: amountHidden && splitSort.sortBy === 'amount' ? 'createdAt' : splitSort.sortBy,
    sortDir: splitSort.sortDir,
  });
  const payments = useMemo(() => query.data?.data ?? [], [query.data?.data]);
  const totalPages = query.data?.totalPages ?? 1;
  const firstPayment = payments[0];

  useEffect(() => {
    if (
      !autoSelectFirst ||
      selectedId ||
      query.isLoading ||
      query.isError ||
      !firstPayment ||
      !window.matchMedia('(min-width: 768px)').matches
    ) {
      return;
    }

    onAutoSelect(firstPayment);
  }, [autoSelectFirst, firstPayment, onAutoSelect, query.isError, query.isLoading, selectedId]);

  return (
    <PaymentsListPanel
      payments={payments}
      timeZone={query.data?.timeZone}
      selectedId={selectedId}
      onSelect={onSelect}
      isLoading={query.isLoading}
      status={filters.status}
      onStatus={onStatus}
      search={filters.q}
      onSearch={onSearch}
      sortBy={splitSort.sortBy}
      sortDir={splitSort.sortDir}
      onSortOption={onSortOption}
      dateFrom={filters.dateFrom}
      dateTo={filters.dateTo}
      paymentMethod={filters.paymentMethod}
      transactionId={filters.transactionId}
      amountOp={filters.amountOp}
      amountValue={filters.amountValue}
      syncStatus={filters.syncStatus}
      onDateChange={onDateChange}
      onAdvancedChange={(patch) => onAdvancedChange({ ...patch, page: 1 })}
      page={filters.page}
      totalPages={totalPages}
      onPage={onPage}
      detailsHidden={detailsHidden}
    />
  );
}
